import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AdminMessages() {
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState({});

  const load = async () => {
    const { data } = await api.get("/admin/messages");
    setMessages(data);
  };
  useEffect(() => { load(); }, []);

  const send = async (id) => {
    const body = (reply[id] || "").trim();
    if (!body) return;
    try {
      await api.post(`/admin/messages/${id}/reply`, { body });
      toast.success("Respuesta enviada");
      setReply({ ...reply, [id]: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteOne = async (id) => {
    if (!window.confirm("¿Eliminar este mensaje?")) return;
    try {
      await api.delete(`/admin/messages/${id}`);
      toast.success("Mensaje eliminado");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteAll = async () => {
    if (!window.confirm(`¿Eliminar TODOS los ${messages.length} mensajes? Esta acción no se puede deshacer.`)) return;
    try {
      await api.delete("/admin/messages");
      toast.success("Todos los mensajes han sido eliminados");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-3">
        <h1 className="text-3xl font-black text-slate-900">Consultas y presupuestos</h1>
        {messages.length > 0 && (
          <Button
            data-testid="delete-all-messages"
            variant="outline"
            onClick={deleteAll}
            className="gap-2 border-red-200 text-red-700 hover:bg-red-50"
          >
            <Trash2 className="h-4 w-4" /> Borrar todos
          </Button>
        )}
      </div>

      <ul className="flex flex-col gap-3" data-testid="messages-list">
        {messages.length === 0 && (
          <li className="rounded-xl border border-slate-200 bg-white p-6 text-center text-slate-500">
            Sin consultas todavía.
          </li>
        )}
        {messages.map((m) => (
          <li key={m.id} className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="mb-2 flex flex-wrap items-center gap-3">
              <MessageSquare className="h-4 w-4 text-[#4d7c0f]" />
              <span className="font-bold text-slate-900">{m.client_name}</span>
              <span className="text-xs text-slate-500">{m.client_email}</span>
              <Badge variant="outline" className="ml-auto">
                {m.kind === "quote" ? "Presupuesto" : "Pregunta"}
              </Badge>
              <Badge variant="outline" className={m.status === "open" ? "bg-yellow-50 text-yellow-700" : "bg-green-50 text-green-700"}>
                {m.status === "open" ? "Pendiente" : "Respondida"}
              </Badge>
              <button
                data-testid={`delete-message-${m.id}`}
                onClick={() => deleteOne(m.id)}
                className="rounded-full p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                aria-label="Eliminar"
                title="Eliminar mensaje"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
            <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-800">{m.body}</p>

            {m.replies?.length > 0 && (
              <ul className="mt-3 flex flex-col gap-2">
                {m.replies.map((r) => (
                  <li key={`${r.at}-${r.by}`} className="rounded-lg border-l-4 border-[#4d7c0f] bg-[#4d7c0f]/5 p-3 text-sm">
                    <div className="text-xs font-bold text-[#4d7c0f]">{r.by} · {new Date(r.at).toLocaleString("es-ES")}</div>
                    <p className="mt-1 text-slate-800">{r.body}</p>
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-3 flex gap-2">
              <Textarea
                data-testid={`reply-${m.id}`}
                value={reply[m.id] || ""}
                onChange={(e) => setReply({ ...reply, [m.id]: e.target.value })}
                placeholder="Escribe una respuesta…"
                className="min-h-[60px] flex-1"
              />
              <Button
                data-testid={`send-reply-${m.id}`}
                onClick={() => send(m.id)}
                className="self-stretch bg-[#4d7c0f] hover:bg-[#3f6212]"
              >
                <Send className="mr-1 h-4 w-4" /> Enviar
              </Button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
